/**
 * Out of Stock Guard — SuiteCommerce (Standard) 2026.1 extension entry point
 * Supplied Energy, LLC   ·   website id 2   ·   shopping app only
 *
 * PURPOSE
 *   Re-create the removed "Website Out Of Stock Items" global control. SuiteCommerce
 *   has no native setting: an item whose Out Of Stock Behavior is "- Default -"
 *   falls through to ALLOW purchase. This makes the site-wide default
 *   "out of stock => not purchasable".
 *
 *   This extension supplies ONLY the default. NetSuite's per-item Out Of Stock
 *   Behavior wins whenever it is set to anything other than "- Default -":
 *     "- Default -"                                    -> block  (our site default)
 *     "Allow back orders ..." (either variant)          -> ALLOW, item stays sellable
 *     "Disallow back orders but display ... message"    -> block
 *     "Remove item when out of stock"                   -> block
 *   Read from the item's `outofstockbehavior` (see behaviorPolicy for label/code
 *   handling). NOTE: this field is NOT on PDP.getItemInfo() — it is in the PDP's own
 *   `fieldset=details` response but the component returns a reduced object, so it is
 *   fetched by item id and cached. See fetchBehavior.
 *
 * FAIL OPEN — we only block when we can POSITIVELY prove an item is out of stock.
 * Anything unknown is allowed. We never kill a real sale.
 *
 * ====================== HOW IT WORKS (verified on test, v1.0.4) ======================
 * Stock is resolved by ITEM ID, not off the add-to-cart payload.
 *   - `oosIds`/`okIds` are populated from PDP.getStockInfo() (+ getItemInfo() for the
 *     id) and from PLP.getItemsInfo() as listings render.
 *   - beforeAddLine extracts the item id and blocks only if that id is known
 *     out of stock. Unknown id => allow (fail open).
 *
 * WHY NOT READ STOCK OFF THE LINE: v1.0.1 and v1.0.2 tried that and both failed open.
 * The line object carries no stock data at all — the data simply isn't there, so no
 * amount of field-name guessing could work.
 *
 * THE PAYLOAD IS A WRAPPER — the mistake that cost three attempts. beforeAddLine
 * receives, captured live on test 2026-07-23:
 *     {"line":{"extras":{...},"item":{"internalid":8605,"itemtype":"InvtPart"},
 *      "quantity":1,"options":[]}}
 * The id is TWO levels down, not on the object handed to us. v1.0.3 looked one level
 * too shallow and found nothing. lineItemId() now unwraps `.line.item.internalid`,
 * with deepFindId() as a defensive fallback if other call sites wrap differently.
 *
 * Layer 2 (PDP button disable) needs the MutationObserver + retry ramp: the TT theme
 * renders asynchronously, so v1.0.1's one-shot setTimeout(...,0) ran before the
 * button existed.
 *
 * ===================== THE v1.0.5 PRODUCTION INCIDENT (2026-07-24) =================
 * v1.0.5 blocked add-to-cart for EVERY product on www, in stock or not. Cause was a
 * latch in the MutationObserver:
 *
 *     if (lastVerdict === true) { markButtons(true); } else { applyPdpState(); }
 *
 * This is a single page app: the module lives for the whole session and the DOM is
 * swapped in place. Once a shopper viewed ONE out-of-stock product, lastVerdict
 * stuck at true, applyPdpState() was never called again, and every later re-render
 * force-disabled the button on every product for the rest of the session.
 *
 * It survived testing because every check was a full page load (direct URL or hard
 * reload), which resets module state. Nobody clicked from an out-of-stock product
 * to another product in-session — the ordinary way people shop.
 *
 * Three guards now, all of which must hold:
 *   1. The observer ALWAYS re-evaluates. No latch.
 *   2. `shownId` detects SPA item changes and releases the previous item's disable
 *      before re-evaluating.
 *   3. The verdict is tri-state; UNKNOWN touches nothing. Only an explicit
 *      isinstock=false ever disables or blocks.
 *
 * REGRESSION TEST — full page loads prove nothing here. Always verify browse →
 * out-of-stock PDP → click to an in-stock PDP, all without reloading.
 * ==================================================================================
 *
 * CONFIRMED (fetched TT_Master_Theme 2026.1):
 *   Add-to-Cart button, Modules/Cart/Templates/cart_add_to_cart_button.tpl:
 *     <button type="submit" data-type="add-to-cart" data-action="sticky"
 *             class="cart-add-to-cart-button-button">
 *   data-TYPE="add-to-cart" (data-action is "sticky"). Renders whenever native
 *   isCurrentItemPurchasable is true — which for OOS "Default" items it still is.
 *
 * SCOPE LIMIT: blocks ADDING only. Does not remove an out-of-stock line already in a
 * cart, and is frontend-only — it does not stop orders placed through other channels.
 *
 * On a block the shopper sees SuiteCommerce's own message ("Sorry, there is a problem
 * with this Item and can not be purchased at this time."); the thrown Error's text is
 * not surfaced. Verification log: docs/out-of-stock-test-then-prod.md
 * ==================================================================================
 */
define(
	'SuppliedEnergy.OutOfStockGuard.OutOfStockGuard'
,	[]
,	function ()
{
	'use strict';

	// OFF — production build. Turn ON only for a test-domain diagnostic cycle, and
	// remember that with logs silenced "working correctly" and "failed to load" look
	// identical from outside: verify behaviourally (button state + a real add), never
	// by absence of console output alone.
	var OOSG_DEBUG = false;

	var ADD_TO_CART_BTN =
		'button[data-type="add-to-cart"].cart-add-to-cart-button-button, ' +
		'.cart-add-to-cart-button-container button[type="submit"]';

	function log () {
		if (!OOSG_DEBUG || typeof console === 'undefined') { return; }
		try { console.log.apply(console, ['[OOSG]'].concat([].slice.call(arguments))); } catch (e) {}
	}

	// --- Field access: plain objects OR Backbone-style models (.get()) ----
	function readField (obj, names) {
		if (!obj || typeof obj !== 'object') { return undefined; }
		for (var i = 0; i < names.length; i++) {
			var n = names[i];
			if (obj[n] !== undefined && obj[n] !== null) { return obj[n]; }
			if (typeof obj.get === 'function') {
				try {
					var v = obj.get(n);
					if (v !== undefined && v !== null) { return v; }
				} catch (e) {}
			}
		}
		return undefined;
	}

	// --- NetSuite's per-item Out Of Stock Behavior ------------------------
	// Classifies the item's own `outofstockbehavior` into what it means for
	// purchasability. Returns 'allow' | 'block' | 'default'.
	//
	// TWO REPRESENTATIONS, both handled. Verified live on 2026-07-24:
	//   commerce API returns the LABEL  -> "Allow back orders with no out-of-stock message"
	//   the item record stores a CODE   -> "ENABLENMSG"
	// The codes are short and share no words with the labels, so matching must cover
	// both. (An earlier version guessed the codes were verbose spellings of the
	// labels; they are not. ENABLENMSG would have fallen through to 'default' and
	// blocked an item the merchant had explicitly made sellable.)
	//
	// The exact labels also use HYPHENS — "out-of-stock", not "out of stock".
	// Normalising to letters-only absorbs that, which is why matching is done on a
	// stripped string rather than on exact values.
	//
	// Confirmed option set (NetSuite item record, Web Store tab):
	//   "- Default -"                                          DEFAULT
	//   "Disallow back orders but display out-of-stock message" DISABLE*
	//   "Allow back orders but display out-of-stock message"    ENABLE*
	//   "Remove item when out-of-stock"                         REMOVE*
	//   "Allow back orders with no out-of-stock message"        ENABLENMSG  (confirmed)
	//   * code inferred; the label path is the one the storefront actually uses.
	function normalizeBehavior (v) {
		if (v == null) { return null; }
		var s = String(v).toLowerCase().replace(/[^a-z]/g, '');
		return s || null;
	}

	function behaviorPolicy (raw) {
		var s = normalizeBehavior(raw);
		if (s === null || s === 'default') { return 'default'; }
		// ORDER MATTERS: "disallowbackorders..." CONTAINS "allowbackorders...", so a
		// naive allow-check first would read every Disallow setting as Allow and let
		// through exactly the items NetSuite says to block. Negatives before
		// positives, for the label forms and the code forms alike.
		if (s.indexOf('disallow') !== -1) { return 'block'; }   // label
		if (s.indexOf('disable')  !== -1) { return 'block'; }   // code
		if (s.indexOf('remove')   !== -1) { return 'block'; }   // both
		if (s.indexOf('allowbackorder') !== -1) { return 'allow'; } // label
		if (s.indexOf('enable')   !== -1) { return 'allow'; }   // code: ENABLE, ENABLENMSG
		return 'default';
	}

	// --- Behavior lookup by item id --------------------------------------
	// PDP.getItemInfo() does NOT expose outofstockbehavior. Verified live
	// 2026-07-24: the field IS in the PDP's own fieldset=details response, but the
	// component hands back a reduced object, so reading it inline silently yielded
	// "(absent)" and every item fell back to the site default.
	//
	// So fetch it by id from the same cacheable endpoint the PDP already requested.
	// The response is already in browser/CDN cache, so this costs ~nothing, and the
	// minimal query below is confirmed sufficient — no account, locale or price
	// level params needed (they don't affect these fields anyway).
	var behaviorCache = {};    // id -> 'allow' | 'block' | 'default'
	var behaviorPending = {};  // id -> true

	function cachedPolicy (id) {
		if (id == null) { return null; }
		id = String(id);
		return Object.prototype.hasOwnProperty.call(behaviorCache, id) ? behaviorCache[id] : null;
	}

	function fetchBehavior (id, done) {
		if (id == null) { return; }
		id = String(id);
		if (cachedPolicy(id) !== null || behaviorPending[id]) { return; }
		if (typeof fetch !== 'function') { behaviorCache[id] = 'default'; return; }
		behaviorPending[id] = true;
		fetch('/api/cacheable/items?id=' + encodeURIComponent(id) + '&fieldset=details',
			{ headers: { 'Accept': 'application/json' } })
			.then(function (r) { return r.json(); })
			.then(function (j) {
				var it = (j && j.items && j.items[0]) || null;
				var raw = it ? readField(it, ['outofstockbehavior', 'outOfStockBehavior']) : null;
				behaviorCache[id] = behaviorPolicy(raw);
				log('behavior fetched id=', id, 'raw=', raw, 'policy=', behaviorCache[id]);
			})
			.catch(function (e) {
				// Stock is known; only the per-item override is unavailable, so the
				// site-wide default is the honest answer — which is what this
				// extension exists to apply.
				behaviorCache[id] = 'default';
				log('behavior fetch failed id=', id, String(e));
			})
			.then(function () {
				delete behaviorPending[id];
				if (typeof done === 'function') { done(); }
			});
	}

	// Tri-state: true = block, false = purchasable, null = UNKNOWN (never block).
	//
	// `isinstock` is the only stock signal we trust. Verified against the live Items
	// API: item 8605 => isinstock false / qty 0; item 882 => isinstock true / qty 32.
	//
	// Deliberately NO quantity fallback. v1.0.5 treated `quantityavailable <= 0` as
	// out of stock, which is wrong for a distributor: drop-ship, non-inventory and
	// backorderable items sit at qty 0 and are perfectly purchasable (both items
	// above are isbackorderable). Unknown must mean ALLOW, or "fails OPEN" is a lie.
	//
	// When an item IS out of stock, NetSuite's per-item Out Of Stock Behavior wins.
	// This extension only supplies the site-wide DEFAULT; an item explicitly set to
	// allow back orders stays purchasable.
	function purchaseVerdict (stock, item, id) {
		var inStock = readField(stock, ['isInStock', 'isinstock']);
		if (inStock == null) { inStock = readField(item, ['isInStock', 'isinstock']); }
		if (inStock === true  || inStock === 'T' || inStock === 'true')  { return false; }
		if (!(inStock === false || inStock === 'F' || inStock === 'false')) { return null; }

		// Prefer the field inline if a caller ever does supply it; otherwise use the
		// fetched-by-id cache.
		var raw = readField(item, ['outofstockbehavior', 'outOfStockBehavior']);
		if (raw == null) { raw = readField(stock, ['outofstockbehavior', 'outOfStockBehavior']); }

		var policy;
		if (raw != null) {
			policy = behaviorPolicy(raw);
			lastBehaviorRaw = String(raw);
		} else {
			var cached = cachedPolicy(id);
			// While the lookup is in flight we apply the site default (block). Being
			// briefly disabled and then enabling is the safe direction; the reverse
			// would let an out-of-stock item be bought before we know better.
			policy = (cached === null) ? 'default' : cached;
			lastBehaviorRaw = (cached === null) ? '(pending lookup)' : '(from id lookup)';
		}
		lastPolicy = policy;
		// 'allow' => item overrides the site default and stays purchasable.
		// 'block'/'default' => not purchasable.
		return policy !== 'allow';
	}

	// Diagnostics only (OOSG_DEBUG), so the honoured setting is visible in logs.
	var lastPolicy = null;
	var lastBehaviorRaw = null;

	// --- Out-of-stock item id registry -----------------------------------
	// The core of the v1.0.3 approach: we learn which item ids are out of stock
	// from the PDP and PLP, then enforce on that at add-to-cart time.
	var oosIds = {};   // id -> true  (positively out of stock)
	var okIds  = {};   // id -> true  (positively in stock)

	function idOf (obj) {
		if (obj == null) { return null; }
		if (typeof obj === 'string' || typeof obj === 'number') { return String(obj); }
		var v = readField(obj, ['internalid', 'internalId', 'itemInternalId', 'id', 'itemid', 'item_id']);
		return (v == null) ? null : String(v);
	}

	// Depth-limited recursive hunt for an item internalid. Necessary because the
	// Extensibility API wraps payloads: beforeAddLine receives
	//   { line: { item: { internalid: 8605, ... }, quantity, options, extras } }
	// i.e. the id is TWO levels down, not on the object handed to us. Confirmed
	// live on test 2026-07-23 via JSON.stringify of the beforeAddLine argument.
	// A recursive search keeps this robust if other call sites wrap differently.
	function deepFindId (obj, depth, seen) {
		depth = depth || 0;
		seen = seen || [];
		if (obj == null || depth > 4) { return null; }
		if (typeof obj === 'string' || typeof obj === 'number') { return null; }
		if (typeof obj !== 'object') { return null; }
		if (seen.indexOf(obj) !== -1) { return null; }
		seen.push(obj);

		// Prefer an id hanging off an `item` node — that's the purchased item.
		var item = readField(obj, ['item']);
		if (item != null) {
			var fromItem = idOf(item);
			if (fromItem != null) { return fromItem; }
		}
		var here = idOf(obj);
		if (here != null) { return here; }

		var keys;
		try { keys = Object.keys(obj); } catch (e) { return null; }
		for (var i = 0; i < keys.length; i++) {
			var got = deepFindId(obj[keys[i]], depth + 1, seen);
			if (got != null) { return got; }
		}
		return null;
	}

	function record (id, oos) {
		if (id == null) { return; }
		id = String(id);
		if (oos) { oosIds[id] = true; delete okIds[id]; }
		else     { okIds[id]  = true; delete oosIds[id]; }
	}

	// Pull the item id out of the beforeAddLine argument. Confirmed shape:
	//   { line: { item: { internalid: 8605, itemtype: "InvtPart", ... },
	//             quantity: 1, options: [], extras: {...} } }
	// Unwrap `.line` first, then read `.item.internalid`; deepFindId covers the
	// rest defensively.
	function lineItemId (arg) {
		if (!arg) { return null; }
		var line = readField(arg, ['line']) || arg;
		var item = readField(line, ['item']);
		var direct = idOf(item);
		if (direct != null) { return direct; }
		return deepFindId(arg, 0, []);
	}

	// --- PDP UX (Layer 2) -------------------------------------------------
	// Only ever RELEASES buttons this extension itself disabled, identified by our
	// own class. The theme disables the button for its own reasons (unselected
	// matrix options, etc.); blanket-removing `disabled` would enable a button the
	// theme deliberately locked.
	function markButtons (oos) {
		if (typeof document === 'undefined') { return 0; }
		var nodes = document.querySelectorAll(ADD_TO_CART_BTN);
		for (var i = 0; i < nodes.length; i++) {
			var btn = nodes[i];
			if (oos) {
				btn.setAttribute('disabled', 'disabled');
				btn.classList.add('oosg-out-of-stock');
			} else if (btn.classList.contains('oosg-out-of-stock')) {
				btn.classList.remove('oosg-out-of-stock');
				btn.removeAttribute('disabled');
			}
		}
		return nodes.length;
	}

	return {
		mountToApp: function mountToApp (container) {
			if (typeof document === 'undefined' || typeof window === 'undefined') { return; }
			if (!container || typeof container.getComponent !== 'function') { return; }

			// ============================================================
			// Layer 1 — enforcement by item id (covers PDP, quick view,
			// grid quick-add, related items — every path funnels here)
			// ============================================================
			var Cart = container.getComponent('Cart');
			if (Cart && typeof Cart.on === 'function') {
				Cart.on('beforeAddLine', function (line) {
					var id = lineItemId(line);
					if (OOSG_DEBUG) {
						var dump = '(unserializable)';
						try { dump = JSON.stringify(line); } catch (e) {}
						log('beforeAddLine id=', id, 'raw=', dump);
					}
					// `=== true` not truthiness: oosIds is a plain object, so an id
					// like "constructor" would otherwise hit Object.prototype and
					// read as truthy.
					if (id != null && oosIds[id] === true) {
						log('BLOCKING out-of-stock add, id=', id);
						throw new Error('This item is out of stock and cannot be added to the cart.');
					}
					log('allowed (id not known out of stock)', id);
				});
			} else {
				log('Cart component unavailable — enforcement inactive');
			}

			// ============================================================
			// Populate the registry from the PLP (product grids / search)
			// ============================================================
			var PLP = container.getComponent('PLP');
			var scanPlp = function scanPlp () {
				if (!PLP || typeof PLP.getItemsInfo !== 'function') { return; }
				// Verified against the live Items API: the listing fieldset carries
				// NEITHER isinstock NOR quantityavailable, so every item here yields
				// an unknown verdict and nothing is recorded. That is correct — a
				// listing page must never be the thing that blocks a sale. Kept so
				// grids start contributing automatically if the fieldset gains stock
				// fields later.
				try {
					var items = PLP.getItemsInfo() || [];
					var n = 0;
					for (var i = 0; i < items.length; i++) {
						var pid = deepFindId(items[i], 0, []);
						var v = purchaseVerdict(items[i], items[i], pid);
						if (v === null) { continue; }
						var id = pid;
						if (id != null) { record(id, v); n++; }
					}
					if (n) { log('PLP scan recorded', n, 'of', items.length, 'items'); }
				} catch (e) { log('PLP scan error', String(e)); }
			};

			// ============================================================
			// Layer 2 — PDP button + registry population for the shown item
			// ============================================================
			var PDP = container.getComponent('PDP');
			var shownId = null;

			var applyPdpState = function applyPdpState () {
				if (!PDP) { return; }

				// Record this item's id so Layer 1 can enforce on it. getItemInfo()
				// wraps its payload the same way beforeAddLine does, so search deeply
				// rather than reading a top-level field.
				var info = (typeof PDP.getItemInfo === 'function') ? PDP.getItemInfo() : null;
				var id = deepFindId(info, 0, []);
				var stock = (typeof PDP.getStockInfo === 'function') ? PDP.getStockInfo() : null;
				var verdict = purchaseVerdict(stock, info, id);

				// Out of stock and we don't yet know this item's own setting: look it
				// up once, then re-evaluate. fetchBehavior de-dupes on both the cache
				// and an in-flight flag, so this cannot loop.
				if (verdict === true && id != null && cachedPolicy(id) === null) {
					fetchBehavior(id, applyPdpState);
				}

				// SPA navigation: the shop is a single page app, so this module lives
				// for the whole session and the DOM is swapped in place. When the shown
				// item changes, release any disable left over from the previous item
				// BEFORE re-evaluating — otherwise one out-of-stock PDP leaks its state
				// onto every product the shopper views afterwards.
				if (id != null && id !== shownId) {
					shownId = id;
					markButtons(false);
				}

				if (id != null && verdict != null) { record(id, verdict); }

				// Unknown verdict => touch nothing. Never disable on missing data.
				var n = 0;
				if (verdict !== null) { n = markButtons(verdict); }
				log('applyPdpState verdict=', verdict, 'id=', id, 'buttons=', n,
					'behavior=', lastBehaviorRaw, 'policy=', lastPolicy);
				if (OOSG_DEBUG && id == null && info) {
					var dump = '(unserializable)';
					try { dump = JSON.stringify(info).slice(0, 600); } catch (e) {}
					log('getItemInfo raw=', dump);
				}
			};

			if (PDP && typeof PDP.on === 'function') {
				PDP.on('afterOptionSelection', applyPdpState);
				PDP.on('afterQuantityChange', applyPdpState);
			} else {
				log('PDP component unavailable — UX layer inactive');
			}

			// The TT theme renders asynchronously, so a single immediate pass misses
			// the button (the v1.0.1 bug). Retry on a short ramp, and rescan the PLP.
			[0, 150, 400, 900, 1800, 3000].forEach(function (ms) {
				setTimeout(function () { applyPdpState(); scanPlp(); }, ms);
			});

			// Re-assert on any re-render (view swaps, SPA navigation, grid paging).
			var target = document.querySelector('#main, .main-container') || document.body;
			var scheduled = false;
			var observer = new MutationObserver(function () {
				if (scheduled) { return; }
				scheduled = true;
				setTimeout(function () {
					scheduled = false;
					// ALWAYS re-evaluate. v1.0.5 latched here — once lastVerdict was
					// true it stopped calling applyPdpState and just force-disabled
					// every button on every later re-render, so after viewing one
					// out-of-stock product NOTHING could be added for the rest of the
					// session. That took production down on 2026-07-24.
					applyPdpState();
					scanPlp();
				}, 120);
			});
			observer.observe(target, { childList: true, subtree: true });
		}
	};
});
